import { useCallback } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import logout from '../../../helpers/logout';
import { config as sharedConfig } from 'lane-shared/config';
import { useAuth0Context } from 'lane-shared/contexts/Auth0Context';
import { useGetAuthToken } from 'hooks/useGetAuthToken';
import { useAuth0WebRedirectURL } from 'hooks/useAuth0WebRedirectURL';

export type UseAuthClearSessionOptions = {
  /** When provided, redirect here after logout instead of auth0WebRedirectURL. Passed via redirect service so Auth0 only sees an allowed URL. */
  returnTo?: string;
  /** When true, emit EVENT_LOGOUT in addition to EVENT_AUTH_TOKEN_INVALID. Default true. */
  emitLogout?: boolean;
};

export function buildRedirectServiceReturnTo(destinationUrl: string): string {
  const { redirectionServiceURL } = sharedConfig.auth0;
  const url = new URL(`https://${redirectionServiceURL}/v1`);

  url.searchParams.set('destination_url', destinationUrl);

  return url.toString();
}

export function useAuthClearSession(options: UseAuthClearSessionOptions = {}) {
  const auth0WebRedirectURL = useAuth0WebRedirectURL();
  const { logout: auth0Logout, isAuthenticated } = useAuth0();
  const { setIsAuth0LogoutInProgress } = useAuth0Context();
  const getAuthToken = useGetAuthToken();
  const { returnTo: overrideReturnTo, emitLogout = true } = options;
  const returnTo =
    overrideReturnTo != null
      ? buildRedirectServiceReturnTo(overrideReturnTo)
      : auth0WebRedirectURL;

  return useCallback(async () => {
    if (isAuthenticated) {
      setIsAuth0LogoutInProgress(true);

      await logout(getAuthToken, {
        emitLogout,
        emitAuthTokenInvalid: true,
      });
      await auth0Logout({
        openUrl: false,
      });
      auth0Logout({
        logoutParams: { returnTo },
      });
    }
  }, [
    isAuthenticated,
    auth0Logout,
    returnTo,
    getAuthToken,
    emitLogout,
    setIsAuth0LogoutInProgress,
  ]);
}
